import {
  BaseItem,
  CanHaveBorderRadius,
  CanHaveCrop,
  CanHaveZoomKeyframes,
  CanHaveAnimation,
  CanHaveRotation,
  RenderMetaBaseItemCore,
} from '../shared';
import {
  EditorItemType,
  EditorStarterItem,
} from '@/features/remotion-editor/editor/items/item-type';
import { ClipType } from '@/shared/apis/editor/clip-types';
import { ReframeMeta } from '@/shared/apis/editor/reframe-types';

export const EMBEDDED_LIPSYNC_AUDIO_PLAYBACK_MODE =
  'embedded_lipsync_video' as const;
export const AVATAR_SEGMENT_SOURCE_ROLE = 'avatar-segment' as const;

export type VideoItemRenderMeta = {
  clipType: Extract<ClipType, 'video'>;
  start: number;
  end: number;
  sceneId?: number;
} & RenderMetaBaseItemCore;

export type VideoItem = BaseItem &
  CanHaveBorderRadius &
  CanHaveCrop &
  CanHaveZoomKeyframes &
  CanHaveAnimation &
  CanHaveRotation & {
    type: Extract<EditorItemType, 'video'>;
    videoStartFromInSeconds: number;
    decibelAdjustment: number;
    playbackRate: number;
    audioFadeInDurationInSeconds: number;
    audioFadeOutDurationInSeconds: number;
    fadeInDurationInSeconds: number;
    fadeOutDurationInSeconds: number;
    assetId: string;
    keepAspectRatio: boolean;
    renderMeta?: VideoItemRenderMeta;
    reframeMeta?: ReframeMeta;
    muteAudio?: boolean;
    /** 영상 내부 음성이 별도 오디오 클립을 대신하는 단일 재생 권위인지 여부. */
    audioPlaybackMode?: string;
    /**
     * 투명(알파) 오버레이 영상 여부. 아바타 오버레이처럼 placement(지정 위치/크기)를
     * 유지해야 하는 영상은 브랜드 템플릿의 main-video 풀스크린 fit 대상에서 제외된다.
     * 소스는 asset.hasAlpha(렌더 시 transparent 처리)와 동일 신호.
     */
    hasAlpha?: boolean;
    /** BE가 명시한 영상 생성 역할. 투명 영상 전체가 아닌 세그먼트 아바타만 식별한다. */
    sourceRole?: string;
    /** 원본 비디오 해상도 (bbox 좌표 변환에 필요) */
    sourceVideoWidth?: number;
    sourceVideoHeight?: number;
    /**
     * defer-baking(v2) 세그먼트 아바타 클립: 클립이 아이템 길이보다 짧으면 반복 재생.
     * one-shot 제스처(인사 등)는 false — 마지막 프레임 유지.
     */
    loop?: boolean;
  };

export const hasEmbeddedLipsyncAudio = (item: VideoItem): boolean =>
  item.audioPlaybackMode === EMBEDDED_LIPSYNC_AUDIO_PLAYBACK_MODE &&
  item.muteAudio !== true;

export const isVideoItem = (item?: EditorStarterItem): item is VideoItem => {
  if (!item) return false;

  return item.type === 'video';
};
